import { useMergeRefs } from "@chakra-ui/react";
import React from "react";
import { useController } from "react-hook-form";
import { NumericFormat, NumericFormatProps } from "react-number-format";
import { usePrefixedFieldName } from "shared/components/form/FormFieldPrefix";
import { useIsFormDisabled } from "shared/components/form/FormStateProvider";
import { DEFAULT_DECIMAL_SCALE } from "shared/components/input/InputConstants";
import {
  SimpleTextInput,
  SimpleTextInputProps,
} from "shared/components/input/text/SimpleTextInput";
import { DollarSignIcon } from "shared/icons/DollarSignIcon";
import { getFieldError } from "shared/utils/FormUtils";
import { parseCurrency, symbolForCurrency } from "shared/utils/MoneyUtils";
import { isAllowedReactNumberFormatValidInt } from "shared/utils/NumberUtils";

export type SimpleMoneyInputProps = {
  // FIXME: Proper currency support (make this prop required)
  //  https://maybern.atlassian.net/browse/ENG-354
  currency?: string;
  showCurrencyIcon?: boolean;
  showCurrencyPrefix?: boolean;
  shouldUnregister?: boolean;
  allowNegative?: boolean;
} & NumericFormatProps<SimpleTextInputProps>;

export type FormSimpleMoneyInputProps = {
  name: string;
  unsetWhenEmpty?: boolean;
  onChangeValue?: (value: string | null) => void;
} & Omit<
  SimpleMoneyInputProps,
  "value" | "onChange" | "name" | "onChangeValue"
>;

export const SimpleMoneyInput = React.memo(
  React.forwardRef<HTMLInputElement, SimpleMoneyInputProps>(
    (
      {
        currency = "USD",
        showCurrencyIcon = true,
        showCurrencyPrefix,
        allowNegative = false,
        decimalScale = DEFAULT_DECIMAL_SCALE,
        onChangeValue,
        ...props
      },
      ref,
    ) => {
      const hasIcon = showCurrencyIcon && currency === "USD";
      return (
        <NumericFormat<SimpleTextInputProps>
          customInput={SimpleTextInput}
          thousandSeparator={true}
          decimalScale={decimalScale}
          getInputRef={ref}
          valueIsNumericString={true}
          allowNegative={allowNegative}
          isAllowed={isAllowedReactNumberFormatValidInt}
          inputLeftElement={hasIcon ? <DollarSignIcon /> : undefined}
          prefix={
            hasIcon || showCurrencyPrefix === false
              ? undefined
              : symbolForCurrency(parseCurrency(currency))
          }
          textAlign="right"
          _placeholder={{
            textAlign: "right",
            color: "gray.400",
          }}
          onValueChange={({ value }) => {
            onChangeValue?.(value);
          }}
          {...props}
        />
      );
    },
  ),
);
SimpleMoneyInput.displayName = "SimpleMoneyInput";

export const FormSimpleMoneyInput = React.forwardRef<
  HTMLInputElement,
  FormSimpleMoneyInputProps
>(
  (
    {
      name,
      isDisabled,
      defaultValue = "",
      unsetWhenEmpty = false,
      shouldUnregister = true,
      onChangeValue,
      ...otherProps
    },
    fwRef,
  ) => {
    const prefixedName = usePrefixedFieldName(name);
    const {
      field: { onChange, ref: fieldRef, ...field },
      formState: { errors },
    } = useController({
      name: prefixedName,
      shouldUnregister,
      defaultValue: defaultValue || (unsetWhenEmpty ? undefined : ""),
    });
    const mergedRef = useMergeRefs(fwRef, fieldRef);
    const isFormDisabled = useIsFormDisabled();
    return (
      <SimpleMoneyInput
        onValueChange={({ value }) => {
          if (field.value !== value) {
            const newValue = !value && unsetWhenEmpty ? null : value;
            onChange(newValue);
            onChangeValue?.(newValue);
          }
        }}
        {...field}
        {...otherProps}
        isDisabled={isDisabled || isFormDisabled}
        error={getFieldError(errors, prefixedName)?.message}
        ref={mergedRef}
      />
    );
  },
);
FormSimpleMoneyInput.displayName = "FormSimpleMoneyInput";
